import React, { useState, createContext, useContext } from "react";
import { MessageCircle, X } from "lucide-react";
import { ChatIcon } from "./DraggableChatIcon";
import { ChatWindow } from "./ChatWindow";
import { ChatErrorBoundary } from "./ChatErrorBoundary";
import { useChat } from "../../hooks/useChat";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { Expand } from "lucide-react";

export interface ChatWidgetConfig {
  // User Metadata
  userMetadata?: {
    name?: string;
    email?: string;
    role?: string;
    department?: string;
    [key: string]: any;
  };

  // API Configuration
  apiUrl?: string;
  botName?: string;
  welcomeMessage?: string;

  // Appearance
  theme?: {
    primaryColor?: string;
    backgroundColor?: string;
    textColor?: string;
    borderRadius?: string;
    themeMode?: "light" | "dark";
  };
  position?: {
    bottom?: string;
    right?: string;
    left?: string;
    top?: string;
  };
  size?: {
    width?: string;
    height?: string;
  };

  // Behavior
  autoOpen?: boolean;
  enableMaximize?: boolean;
  enableMinimize?: boolean;
  enableSounds?: boolean;
  rateLimiting?: {
    maxRequests?: number;
    windowMs?: number;
  };

  // Integration
  customHeaders?: Record<string, string>;
  getAuthToken?: () => string | Promise<string>;
  onMessage?: (message: string) => void;
  onTokenExpired?: () => Promise<void>;
  onResponse?: (response: string) => void;
  onError?: (error: any) => void;
  onOpen?: () => void;
  onClose?: () => void;

  // Chat Configuration
  initialMessages?: Array<{
    content: string;
    sender: "user" | "assistant";
    timestamp?: Date;
  }>;
  placeholder?: string;
  enableTypingIndicator?: boolean;
  enableFileUpload?: boolean;
  maxMessageLength?: number;
  containerElement?: string | HTMLElement;
  zIndex?: number;
  accessMode?: "user" | "admin";
}

const ChatWidgetContext = createContext<ChatWidgetConfig | null>(null);

export const useChatWidgetConfig = () => {
  const context = useContext(ChatWidgetContext);
  return context || {};
};

interface ChatWidgetProps {
  config?: ChatWidgetConfig;
}

export const ChatWidget: React.FC<ChatWidgetProps> = ({ config = {} }) => {
  const [isOpen, setIsOpen] = useLocalStorage(
    "chat-widget-open",
    config.autoOpen || false
  );
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useLocalStorage(
    "chat-widget-maximized",
    false
  );

  // Pass config to chat hook
  const chat = useChat({
    userMetadata: config.userMetadata,
    apiUrl: config.apiUrl,
    customHeaders: config.customHeaders,
    getAuthToken: config.getAuthToken,
    onTokenExpired: config.onTokenExpired,
    onMessage: config.onMessage,
    onResponse: config.onResponse,
    onError: config.onError,
  });

  const isDark = config.theme?.themeMode === "dark";

  const handleOpen = () => {
    setIsOpen(true);
    config.onOpen?.();
  };

  const handleClose = () => {
    setIsOpen(false);
    config.onClose?.();
  };

  const positionStyle = {
    bottom: config.position?.bottom || "1rem",
    right: config.position?.right || "1rem",
    left: config.position?.left,
    top: config.position?.top,
    zIndex: config.zIndex || 50,
  };

  if (!isOpen) {
    return (
      <ChatWidgetContext.Provider value={config}>
        <ChatIcon
          onClick={handleOpen}
          isConnected={chat.isConnected}
          style={positionStyle}
        />
      </ChatWidgetContext.Provider>
    );
  }

  if (isMinimized && config.enableMinimize !== false) {
    return (
      <ChatWidgetContext.Provider value={config}>
        <div className="fixed" style={positionStyle}>
          <div
            className={`rounded-lg shadow-lg p-3 flex items-center gap-2 min-w-[200px] border ${
              isDark
                ? "bg-[#14161C] border-[#1C1F24] text-gray-100"
                : "bg-white border-gray-200 text-gray-800"
            }`}
            style={{
              borderRadius: config.theme?.borderRadius || "0.5rem",
            }}
          >
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: config.theme?.primaryColor || "#f97316",
              }}
            >
              <MessageCircle className="w-3 h-3 text-white" />
            </div>
            <span
              className={`p-1 rounded-md transition-colors ${
                isDark
                  ? "text-gray-300 hover:text-white hover:bg-gray-300/5"
                  : "text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              }`}
            >
              {config.botName || "NopalCyber Assistant"}
            </span>
            <button
              onClick={() => setIsMinimized(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <Expand className="w-4 h-4" />
            </button>
            <button
              onClick={handleClose}
              className={`p-1 rounded-md transition-colors ${
                isDark
                  ? "text-gray-300 hover:text-red-400 hover:bg-gray-300/5"
                  : "text-gray-400 hover:text-red-600 hover:bg-gray-100"
              }`}     
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </ChatWidgetContext.Provider>
    );
  }

  return (
    <ChatWidgetContext.Provider value={config}>
      <ChatErrorBoundary onReset={() => chat.clearMessages()}>
        <ChatWindow
          chat={chat}
          onClose={handleClose}
          onMinimize={
            config.enableMinimize !== false
              ? () => setIsMinimized(true)
              : undefined
          }
          onMaximize={
            config.enableMaximize !== false
              ? () => setIsMaximized(!isMaximized)
              : undefined
          }
          isMaximized={isMaximized}
          style={positionStyle}
          config={config}
        />
      </ChatErrorBoundary>
    </ChatWidgetContext.Provider>
  );
};

// Static method for external initialization
export const ChatWidgetInit = {
  init: (config: ChatWidgetConfig = {}) => {
    const targetElement = config.containerElement
      ? typeof config.containerElement === "string"
        ? document.querySelector(config.containerElement)
        : config.containerElement
      : document.body;

    if (!targetElement) {
      console.error("ChatWidget: Target container element not found");
      return null;
    }

    // Create a div container for the chat widget
    const widgetContainer = document.createElement("div");
    widgetContainer.id = "nopal-chat-widget-container";
    targetElement.appendChild(widgetContainer);

    // Import React and ReactDOM dynamically for external usage
    const windowWithReact = window;
    if (
      typeof window !== "undefined" &&
      windowWithReact.React &&
      windowWithReact.ReactDOM
    ) {
      const React = windowWithReact.React;
      const ReactDOM = windowWithReact.ReactDOM;
      const root = ReactDOM.createRoot
        ? ReactDOM.createRoot(widgetContainer)
        : null;

      if (root) {
        root.render(React.createElement(ChatWidget, { config }));
      } else {
        // Fallback for older React versions
        ReactDOM.render(
          React.createElement(ChatWidget, { config }),
          widgetContainer
        );
      }

      return {
        destroy: () => {
          if (root) {
            root.unmount();
          } else {
            ReactDOM.unmountComponentAtNode(widgetContainer);
          }
          targetElement.removeChild(widgetContainer);
        },
        updateConfig: (newConfig: Partial<ChatWidgetConfig>) => {
          const updatedConfig = { ...config, ...newConfig };
          if (root) {
            root.render(
              React.createElement(ChatWidget, { config: updatedConfig })
            );
          } else {
            ReactDOM.render(
              React.createElement(ChatWidget, { config: updatedConfig }),
              widgetContainer
            );
          }
        },
      };
    } else {
      console.error(
        "ChatWidget: React and ReactDOM must be loaded globally for external usage"
      );
      return null;
    }
  },
};
